import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { SharedProjectView, SharedRecommendedDesign, SharedWall } from '../../../services';
import { ShareBrandFooter } from './ShareBrandFooter.component';
import { ShareBrandHeader } from './ShareBrandHeader.component';

// #region ShareLockedViewProps
export interface ShareLockedViewProps {
    project: SharedProjectView;
}

// #region LockedWallRecap
// One design per wall — show the chosen design and its AI mockup side by side,
// each taking half the full width.
const LockedWallRecap: React.FunctionComponent<{ wall: SharedWall }> = (props): JSX.Element => {
    const { wall } = props;
    const chosen: SharedRecommendedDesign | undefined = wall.recommended_designs.find(
        (design: SharedRecommendedDesign) => wall.selected_design_ids.includes(design.design_id)
    );

    const mockupReady = chosen?.mockup_status === 'GENERATED' && !!chosen.ai_mockup_signed_url;

    return (
        <section className="rounded-3xl border border-share-border bg-share-raised p-4 shadow-sm sm:p-6">
            <h2 className="text-lg font-semibold tracking-tight text-share-text">{wall.name}</h2>
            {!chosen ? (
                <div className="mt-3 text-sm text-share-muted">No selection for this wall.</div>
            ) : (
                <>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="flex flex-col gap-1.5">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-share-muted">Design</div>
                            <div className="aspect-square overflow-hidden rounded-2xl border border-share-border bg-share-surface">
                                {chosen.thumbnail_url ? (
                                    <img
                                        src={chosen.thumbnail_url}
                                        alt={chosen.title || 'Untitled'}
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-xs text-share-muted">
                                        No preview
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-share-muted">On your wall</div>
                            <div className="aspect-square overflow-hidden rounded-2xl border border-share-border bg-share-surface">
                                {mockupReady ? (
                                    <img
                                        src={chosen.ai_mockup_signed_url as string}
                                        alt={`${chosen.title || 'Design'} on your wall`}
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-share-muted">
                                        {chosen.mockup_status === 'FAILED' ? 'Preview unavailable' : 'Preview still generating'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 truncate text-sm font-medium text-share-text">
                        {chosen.title || 'Untitled'}
                    </div>
                </>
            )}
        </section>
    );
};

// #region ShareLockedView
// Read-only, branded thank-you view shown once selections are confirmed.
export const ShareLockedView: React.FunctionComponent<ShareLockedViewProps> = (props: ShareLockedViewProps): JSX.Element => {
    const { project } = props;
    const designer = project.branding.contact_name;

    return (
        <div className="space-y-6">
            <ShareBrandHeader
                branding={project.branding}
                clientName={project.client_name}
                headlineOverride={`Thank you, ${project.client_name}!`}
                bodyOverride={designer
                    ? `Your selections have been sent to ${designer}. Here's a recap of everything you chose.`
                    : 'Your selections have been sent to your designer. Here is a recap of everything you chose.'}
            />

            <div className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                Selections confirmed
            </div>

            {project.walls.map((wall: SharedWall) => (
                <LockedWallRecap key={wall.id} wall={wall} />
            ))}

            <ShareBrandFooter branding={project.branding} />
        </div>
    );
};
